import { useGetAllDirectorsInCompany } from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import {
  Button,
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import {
  BoardPositionEnum,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { WorkRequestType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { omitLegacyOwnershipFlags } from '../../common/helpers';
import {
  findDirectorByRoleForCorporation,
  getDirectorName,
  groupTitlesByDirectorIds,
} from '../../components/Director/common';
import { rolesSchema, RolesSchemaFields } from '../../schema/base/roles';
import { DirectorFormType } from '../Director/DirectorFormType';
import DirectorModal from '../Director/new/DirectorModal';
import { validateDirectorInfo as validateUSDirector } from '../us/validators';
import { IncorporationFormHeading } from './IncorporationFormElements';

type TRolesEnum =
  | BoardPositionEnum.President
  | BoardPositionEnum.Secretary
  | BoardPositionEnum.Treasurer;

const RolesKS = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  formHeading,
  addNewForm,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
  formHeading?: string;
  addNewForm: DirectorFormType<DirectorBase>;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const {
    data: allDirectorsInCompany,
    error,
    mutate: allDirectorsInCompanyMutate,
  } = useGetAllDirectorsInCompany(data?.companyId);

  const directorValidatorMap = {
    [WorkRequestType.USIncorporation]: validateUSDirector,
  };

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const [editDirector, setEditDirector] = useState<DirectorBase>();

  const [addNewType, setAddNewType] = useState<TRolesEnum>();

  const presidentDirector = findDirectorByRoleForCorporation(
    allDirectorsInCompany?.directors,
    BoardPositionEnum.President,
    data?.corporationId
  );

  const secretaryDirector = findDirectorByRoleForCorporation(
    allDirectorsInCompany?.directors,
    BoardPositionEnum.Secretary,
    data?.corporationId
  );

  const treasurerDirector = findDirectorByRoleForCorporation(
    allDirectorsInCompany?.directors,
    BoardPositionEnum.Treasurer,
    data?.corporationId
  );

  const directorOptions = allDirectorsInCompany?.directors?.map((director) => ({
    value: director.id.toString(),
    label: getDirectorName(director),
  }));

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<RolesSchemaFields>({
    resolver: zodResolver(rolesSchema),
    defaultValues: {
      President: presidentDirector?.id?.toString(),
      Secretary: secretaryDirector?.id?.toString(),
      Treasurer: treasurerDirector?.id?.toString(),
    },
  });

  const onEditShareholderDetais = (dirId, boardPosition: TRolesEnum) => {
    const selectedDirector = allDirectorsInCompany?.directors?.find(
      (dir) => dir.id === dirId
    );
    setAddNewType(boardPosition);
    if (selectedDirector) {
      setEditDirector({
        id: selectedDirector.id,
        directorFirstName: selectedDirector.directorFirstName,
        directorLastName: selectedDirector.directorLastName,
        number: selectedDirector.number,
        email: selectedDirector.email,
        dateOfBirth: selectedDirector.dateOfBirth,
        personalDetails: selectedDirector.personalDetails,
        operatingAddress: selectedDirector.operatingAddress,
        corporationHasDirector: selectedDirector.corporationHasDirector,
      });
      setDirectorModalShown(true);
    }
  };

  const onSubmit = async (formData: RolesSchemaFields) => {
    const directorIdsVsTitles = groupTitlesByDirectorIds(formData);

    const directorsBody = [];

    if (data?.requestType in directorValidatorMap) {
      const formError = directorValidatorMap[data?.requestType](
        allDirectorsInCompany?.directors,
        [
          parseInt(formData.President, 10),
          parseInt(formData.Secretary, 10),
          parseInt(formData.Treasurer, 10),
        ]
      );

      if (formError.length) {
        setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));
        setNotification({
          show: true,
          valid: false,
          message: `Please fill in the missing information for partners by clicking on "Edit".`,
          title: 'Error',
        });
        return;
      }
    } else {
      setNotification({
        show: true,
        valid: false,
        message: `Director validator function not found.`,
        title: 'Error',
      });
      return;
    }

    Object.entries(directorIdsVsTitles).forEach(
      ([directorId, directorTitles]) => {
        const director = allDirectorsInCompany?.directors?.find(
          (dir) => dir.id === +directorId
        );
        const corporationHasDirector = director?.corporationHasDirector?.find(
          ({ corporationId }) =>
            corporationId === data.corporationId && +directorId
        );

        const additionalDirectorInformation = {
          ...omitLegacyOwnershipFlags(
            corporationHasDirector?.additionalDirectorInformation
          ),
          boardPosition: (directorTitles as BoardPositionEnum[]).map(
            (each) => BoardPositionEnum[each]
          ),
        };

        directorsBody.push({
          id: director.id,
          corporationHasDirector: [
            {
              ...corporationHasDirector,
              corporationId: data?.corporationId,
              additionalDirectorInformation,
            },
          ],
        });
      }
    );

    // Remove board positions from other directors of same corporation
    const selectedDirectorIds = Object.values(formData);
    allDirectorsInCompany.directors.forEach((dir) => {
      if (!selectedDirectorIds.includes(dir.id.toString())) {
        const corporationHasDirector = dir?.corporationHasDirector?.find(
          ({ corporationId, directorId }) =>
            corporationId === data.corporationId && dir.id === directorId
        );
        const additionalDirectorInformation = omitLegacyOwnershipFlags(
          corporationHasDirector?.additionalDirectorInformation
        );
        if (additionalDirectorInformation.boardPosition?.length) {
          directorsBody.push({
            id: dir.id,
            corporationHasDirector: [
              {
                ...corporationHasDirector,
                corporationId: data?.corporationId,
                additionalDirectorInformation: {
                  ...additionalDirectorInformation,
                  boardPosition: [],
                },
              },
            ],
          });
        }
      }
    });

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          directors: directorsBody,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      return;
    }

    await mutate();

    next();
  };

  useEffect(() => {
    if (!isDirectorModalShown) {
      setEditDirector(undefined);
    }
  }, [isDirectorModalShown]);

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text={formHeading || 'Add company officers'}
        additionalText="Please provide information about this corporation's officers. All roles must have an individual assigned, but the same person can serve in each role if needed."
      />
      <FieldWrapper>
        <Label>
          President <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="president-input"
          options={directorOptions || []}
          value={watch('President')}
          setValue={(val) => setValue('President', val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new officer',
            onAddItem: () => {
              setDirectorModalShown(true);
              setAddNewType(BoardPositionEnum.President);
            },
          }}
          error={
            !watch('President') && errors?.President?.message
              ? errors?.President?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(parseInt(watch('President'), 10)) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditShareholderDetais(
                    parseInt(watch('President'), 10),
                    BoardPositionEnum.President
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Treasurer <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="treasurer-input"
          options={directorOptions || []}
          value={watch('Treasurer')}
          setValue={(val) => setValue('Treasurer', val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new officer',
            onAddItem: () => {
              setDirectorModalShown(true);
              setAddNewType(BoardPositionEnum.Treasurer);
            },
          }}
          error={
            !watch('Treasurer') && errors?.Treasurer?.message
              ? errors?.Treasurer?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(parseInt(watch('Treasurer'), 10)) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditShareholderDetais(
                    parseInt(watch('Treasurer'), 10),
                    BoardPositionEnum.Treasurer
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Secretary <span className="text-text-violet-tertiary">*</span>
        </Label>
        <SingleCombobox
          id="secretart-input"
          options={directorOptions || []}
          value={watch('Secretary')}
          setValue={(val) => setValue('Secretary', val)}
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder: 'Add new officer',
            onAddItem: () => {
              setDirectorModalShown(true);
              setAddNewType(BoardPositionEnum.Secretary);
            },
          }}
          error={
            !watch('Secretary') && errors?.Secretary?.message
              ? errors?.Secretary?.message
              : null
          }
        />
        <div className="flex gap-2 items-center text-sm text-text-tertiary">
          {directorIdsWithError.has(parseInt(watch('Secretary'), 10)) ? (
            <>
              <InputErrorMessage message="Missing details." />
              <span
                className="hover:underline cursor-pointer"
                onClick={() =>
                  onEditShareholderDetais(
                    parseInt(watch('Secretary'), 10),
                    BoardPositionEnum.Secretary
                  )
                }
              >
                Edit here.
              </span>
            </>
          ) : null}
        </div>
      </FieldWrapper>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={handleSubmit(onSubmit, (err) => console.error(err))}
          disabled={isSubmitting}
        >
          <span>Continue</span>
          <ProceedIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <DirectorModal
        isOpen={isDirectorModalShown}
        setOpen={setDirectorModalShown}
        companyDataMutate={allDirectorsInCompanyMutate}
        DirectorForm={addNewForm}
        setNotification={setNotification}
        companyId={data?.companyId}
        corporationId={data?.corporationId}
        director={editDirector}
        token={token}
        modalConfig={{
          heading: 'Add new officer',
          postSave: (dir) => {
            setValue(addNewType, dir.id.toString());
            setDirectorIdsWithError((prevIds) => {
              const newIds = new Set(prevIds);
              newIds.delete(dir.id);
              return newIds;
            });
          },
        }}
        workRequestData={data}
      />
    </div>
  );
};

export default RolesKS;
